import { SwapOutlined } from "@ant-design/icons";
import { Button, Empty, Pagination, Table, Tooltip } from "antd";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { SideBarEndService } from "apis/Services/SideBarService";
import LoadingComponent from "components/loading-component";
import { AuthStorage } from "utils/auth-storage";
import styles from "./table.module.css";

const PER_PAGE = 100;
const DEFAULT_VIEW_NAME_WIDTH = 250;
const MIN_VIEW_NAME_WIDTH = 180;
const MAX_VIEW_NAME_WIDTH = 500;
const FIXED_COLUMNS_WIDTH = 630;

const clampViewNameWidth = (width) =>
  Math.min(MAX_VIEW_NAME_WIDTH, Math.max(MIN_VIEW_NAME_WIDTH, width));

const ResizableHeaderCell = ({
  resizable,
  width,
  onResize,
  onReset,
  children,
  ...restProps
}) => {
  const cleanupRef = useRef(() => {});

  useEffect(
    () => () => {
      cleanupRef.current();
    },
    [],
  );

  const startResize = (event) => {
    if (!resizable) return;
    event.preventDefault();
    event.stopPropagation();
    cleanupRef.current();

    const startX = event.clientX;
    const startWidth = width;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;

    const handleMouseMove = (moveEvent) => {
      onResize(
        clampViewNameWidth(startWidth + moveEvent.clientX - startX),
      );
    };
    const handleMouseUp = () => cleanupRef.current();
    const cleanup = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      cleanupRef.current = () => {};
    };

    cleanupRef.current = cleanup;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const resizeWithKeyboard = (event) => {
    if (!resizable || !["ArrowLeft", "ArrowRight"].includes(event.key))
      return;
    event.preventDefault();
    onResize(
      clampViewNameWidth(
        width + (event.key === "ArrowRight" ? 20 : -20),
      ),
    );
  };

  return (
    <th {...restProps} style={{ ...restProps.style, width }}>
      {children}
      {resizable ? (
        <span
          className={styles.resizeHandle}
          role="separator"
          aria-label="Resize View Name column"
          aria-orientation="vertical"
          aria-valuemin={MIN_VIEW_NAME_WIDTH}
          aria-valuemax={MAX_VIEW_NAME_WIDTH}
          aria-valuenow={width}
          tabIndex={0}
          onMouseDown={startResize}
          onKeyDown={resizeWithKeyboard}
          onDoubleClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onReset();
          }}
        />
      ) : null}
    </th>
  );
};

function getInitials(fullName = "") {
  const nameParts = String(fullName).trim().split(/\s+/).filter(Boolean);

  if (!nameParts.length) {
    return "?";
  }

  return nameParts
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
}

function formatTimestamp(timestamp = "") {
  if (!timestamp) {
    return "Unknown";
  }

  const [datePart = "", timePart = ""] = String(timestamp).split(" ");
  const [month, day, year] = datePart.split("-");
  const [hours = "0", minutes = "0", seconds = "0"] = timePart.split(":");
  const utcDate = new Date(
    Date.UTC(year, Number(month) - 1, day, hours, minutes, seconds),
  );

  if (Number.isNaN(utcDate.getTime())) {
    return timestamp;
  }

  const localizedDate = utcDate.toLocaleDateString(undefined, {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
  const localizedTime = utcDate.toLocaleTimeString();
  const timeZoneName = utcDate
    .toLocaleString("en", { timeZoneName: "short" })
    .split(" ")
    .pop();

  return [localizedDate, localizedTime, timeZoneName]
    .filter(Boolean)
    .join(" • ");
}

function normalizeActivity(activity = {}, index = 0) {
  const viewName = activity?.viewName || "Untitled View";
  const columnName = activity?.columnName || "";
  const fullName = activity?.fullName || "Unknown User";
  const action = activity?.action || "No action details";
  const timestamp = activity?.timestamp || "";

  return {
    key: [viewName, columnName, fullName, timestamp, action, index].join("__"),
    viewName,
    columnName,
    fullName,
    action,
    timestamp,
    formattedTimestamp: formatTimestamp(timestamp),
  };
}

const CompanyViewsNotificationTable = ({ company_id }) => {
  const [activities, setActivities] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [viewNameColumnWidth, setViewNameColumnWidth] = useState(
    DEFAULT_VIEW_NAME_WIDTH,
  );

  const code = AuthStorage.getItem("token");
  const email = AuthStorage.getItem("email");

  useEffect(() => {
    let isActive = true;

    if (!company_id) {
      setActivities([]);
      setIsLoading(false);
      return () => {
        isActive = false;
      };
    }

    setIsLoading(true);
    setHasError(false);

    SideBarEndService.CompanyViewsNotificationCenter({
      code,
      company_id,
      loginEmail: email,
      Page: 1,
      Per_Page: 20000,
    })
      .then((response) => {
        if (!isActive) {
          return;
        }

        setActivities(Array.isArray(response) ? response : []);
      })
      .catch(() => {
        if (isActive) {
          setActivities([]);
          setHasError(true);
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [code, company_id, email, reloadKey]);

  const normalizedRows = useMemo(
    () =>
      activities.map((activity, index) => normalizeActivity(activity, index)),
    [activities],
  );

  const totalItems = normalizedRows.length;
  const paginatedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * PER_PAGE;
    return normalizedRows.slice(startIndex, startIndex + PER_PAGE);
  }, [currentPage, normalizedRows]);

  useEffect(() => {
    const maxPages = Math.max(1, Math.ceil(totalItems / PER_PAGE));

    if (currentPage > maxPages) {
      setCurrentPage(maxPages);
    }
  }, [currentPage, totalItems]);

  const columns = useMemo(
    () => [
      {
        title: "Person",
        dataIndex: "fullName",
        key: "fullName",
        width: 90,
        align: "center",
        render: (value) => (
          <Tooltip title={value} placement="top">
            <div className={styles.personCell}>
              <span className={styles.personAvatar}>{getInitials(value)}</span>
            </div>
          </Tooltip>
        ),
      },
      {
        title: "View Name",
        dataIndex: "viewName",
        key: "viewName",
        width: viewNameColumnWidth,
        className: styles.viewNameColumn,
        ellipsis: true,
        onHeaderCell: () => ({
          width: viewNameColumnWidth,
          resizable: true,
          onResize: setViewNameColumnWidth,
          onReset: () => setViewNameColumnWidth(DEFAULT_VIEW_NAME_WIDTH),
        }),
        render: (value, record) => (
          <div className={styles.viewCell}>
            <Tooltip title={value} placement="top">
              <span className={styles.primaryText}>{value}</span>
            </Tooltip>
            <Tooltip title={record.formattedTimestamp} placement="top">
              <span className={styles.timestamp}>
                {record.formattedTimestamp}
              </span>
            </Tooltip>
          </div>
        ),
      },
      {
        title: "Column Name",
        dataIndex: "columnName",
        key: "columnName",
        width: 180,
        ellipsis: true,
        render: (value) => (
          <Tooltip title={value || "No column"} placement="top">
            <span className={value ? styles.columnBadge : styles.emptyValue}>
              {value || "—"}
            </span>
          </Tooltip>
        ),
      },
      {
        title: "Action",
        dataIndex: "action",
        key: "action",
        width: 360,
        render: (value) => (
          <Tooltip title={value} placement="topLeft">
            <span className={styles.actionText}>{value}</span>
          </Tooltip>
        ),
      },
    ],
    [viewNameColumnWidth],
  );

  const startIndex = totalItems === 0 ? 0 : (currentPage - 1) * PER_PAGE + 1;
  const endIndex = Math.min(currentPage * PER_PAGE, totalItems);

  return (
    <div className={styles.wrapper}>
      {!isLoading && !hasError && totalItems > 0 ? (
        <div className={styles.scrollHint} role="note">
          <SwapOutlined aria-hidden="true" />
          <span>Scroll horizontally · Drag the View Name edge to resize</span>
        </div>
      ) : null}

      <div className={styles.tableCard}>
        {isLoading ? (
          <div className={styles.loadingState}>
            <LoadingComponent />
          </div>
        ) : hasError ? (
          <div className={styles.emptyState}>
            <Empty description="We couldn't load view notifications right now.">
              <Button onClick={() => setReloadKey((key) => key + 1)}>
                Try again
              </Button>
            </Empty>
          </div>
        ) : totalItems === 0 ? (
          <div className={styles.emptyState}>
            <Empty description="No custom view notifications yet." />
          </div>
        ) : (
          <>
            <Table
              columns={columns}
              dataSource={paginatedRows}
              pagination={false}
              rowKey="key"
              className={styles.notificationTable}
              size="small"
              tableLayout="fixed"
              components={{
                header: { cell: ResizableHeaderCell },
              }}
              scroll={{ x: FIXED_COLUMNS_WIDTH + viewNameColumnWidth }}
            />

            <div className={styles.footer}>
              <small className={styles.footerSummary}>
                Showing {startIndex}-{endIndex} of {totalItems} notifications
              </small>

              <Pagination
                current={currentPage}
                total={totalItems}
                pageSize={PER_PAGE}
                showSizeChanger={false}
                onChange={setCurrentPage}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CompanyViewsNotificationTable;
